import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '@/lib/api';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { RegistreCave } from '@/components/cave/RegistreCave';
import { 
  Wine, Thermometer, Droplets, FlaskConical, Package, 
  BarChart3, AlertTriangle, Plus, RefreshCw, Beaker,
  Grape, Calendar, FileText, TrendingUp, Box, Tag,
  Bot, Send, Sparkles, MessageCircle, User, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CavePage() {
  const { activeSociete } = useWorkspace();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('cuves');
  useEffect(() => {
    const t = new URLSearchParams(location.search).get('tab');
    if (t) setActiveTab(t);
  }, [location.search]);
  const [cuves, setCuves] = useState([]);
  const [intrants, setIntrants] = useState([]);
  const [matieres, setMatieres] = useState([]);
  const [lots, setLots] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  
  // État pour l'agent œnologue
  const [oenologueMessages, setOenologueMessages] = useState([
    {
      role: 'assistant',
      content: `🍷 Bonjour ! Je suis **Maître Bacchus**, votre œnologue IA expert.

Je suis spécialisé dans les vins de la Vallée du Rhône, notamment les appellations **Vinsobres** et **Côtes du Rhône**.

Je peux vous conseiller sur :
- La **vinification** (fermentation, macération, températures)
- Les **assemblages** et cuvées
- L'**élevage** en fût ou en cuve
- L'**analyse sensorielle** et la dégustation
- Les **traitements œnologiques** (sulfitage, collage, filtration)
- La **mise en bouteille** et le potentiel de garde

Comment puis-je vous aider aujourd'hui ?`
    }
  ]);
  const [oenologueInput, setOenologueInput] = useState('');
  const [oenologueLoading, setOenologueLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [activeSociete]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [oenologueMessages]);

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, ...(localStorage.getItem('activeSocieteId') ? { 'X-Societe-Id': localStorage.getItem('activeSocieteId') } : {}) };

    try {
      // Fetch cuves
      const cuvesRes = await fetch(`${API_URL}/api/cave/cuves`, { headers });
      if (cuvesRes.ok) {
        const data = await cuvesRes.json();
        setCuves(data.cuves || []);
        setStats(data.stats || {});
      }

      // Fetch intrants
      const intrantsRes = await fetch(`${API_URL}/api/cave/intrants`, { headers });
      if (intrantsRes.ok) {
        const data = await intrantsRes.json();
        setIntrants(data.intrants || []);
      }

      // Fetch matières sèches
      const matieresRes = await fetch(`${API_URL}/api/cave/matieres-seches`, { headers });
      if (matieresRes.ok) {
        const data = await matieresRes.json();
        setMatieres(data.matieres_seches || []);
      }

      // Fetch lots
      const lotsRes = await fetch(`${API_URL}/api/cave/lots`, { headers });
      if (lotsRes.ok) {
        const data = await lotsRes.json();
        setLots(data.lots || []);
      }

      // Fetch alertes IA
      const alertesRes = await fetch(`${API_URL}/api/cave/ia/alertes`, { headers });
      if (alertesRes.ok) {
        const data = await alertesRes.json();
        setAlertes(data.alertes || []);
      }
    } catch (error) {
      console.error('Erreur chargement données cave:', error);
    }
    setLoading(false);
  };

  // Envoyer un message à l'œnologue IA
  const sendToOenologue = async () => {
    if (!oenologueInput.trim() || oenologueLoading) return;

    const userMessage = oenologueInput.trim();
    setOenologueInput('');
    setOenologueMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setOenologueLoading(true);

    try {
      const response = await api.post('/brain/chat', {
        message: userMessage,
        agent_type: 'oenologue',
        context: {
          cuves: cuves.slice(0, 5),
          lots: lots.slice(0, 5),
          societe: activeSociete?.nom
        }
      });

      const data = response.data;
      setOenologueMessages(prev => [...prev, { 
        role: 'assistant', 
        content: data.response || data.message || "Je réfléchis à votre question..."
      }]);
    } catch (error) {
      // Réponse simulée en cas d'erreur
      const simulatedResponse = generateOenologueResponse(userMessage);
      setOenologueMessages(prev => [...prev, { 
        role: 'assistant', 
        content: simulatedResponse
      }]);
    }
    
    setOenologueLoading(false);
  };

  // Générer une réponse simulée de l'œnologue
  const generateOenologueResponse = (question) => {
    const q = question.toLowerCase();
    
    if (q.includes('température') || q.includes('fermentation')) {
      return `🌡️ **Conseil température de fermentation**

Pour vos vins de Vinsobres, je recommande :

**Vins rouges (Grenache/Syrah)** :
- Fermentation alcoolique : **24-28°C** pour extraire couleur et arômes
- Phase de macération : **26-30°C** maximum pour les tanins
- Durée : 15-21 jours selon la structure souhaitée

**Conseil pratique** : Surveillez la température 2x/jour et utilisez un système de régulation si possible. Une montée trop rapide au-dessus de 30°C peut créer des arômes de cuit.

Avez-vous des cuves thermorégulées ?`;
    }
    
    if (q.includes('sulfite') || q.includes('so2') || q.includes('soufre')) {
      return `⚗️ **Gestion du SO2 (sulfites)**

Pour l'appellation Vinsobres AOC, voici mes recommandations :

**À la vendange** :
- 3-5 g/hL de SO2 sur raisins sains
- 5-8 g/hL si botrytis présent

**Post-fermentation** :
- Viser **25-35 mg/L de SO2 libre** pour les rouges
- Ajuster selon le pH (plus le pH est élevé, plus il faut de SO2)

**Avant mise** :
- SO2 libre : 25-30 mg/L
- SO2 total max AOC : 150 mg/L (rouges)

💡 *Astuce* : Mesurez le SO2 libre toutes les 2-3 semaines pendant l'élevage.`;
    }
    
    if (q.includes('assemblage') || q.includes('cuvée') || q.includes('blend')) {
      return `🍇 **Conseils d'assemblage pour Vinsobres**

L'AOC Vinsobres impose un minimum de **50% Grenache**, complété par Syrah et/ou Mourvèdre.

**Mon conseil pour une cuvée équilibrée** :
- **60% Grenache** : fruit, rondeur, alcool
- **30% Syrah** : couleur, épices, structure
- **10% Mourvèdre** : complexité, garde

**Méthode d'assemblage** :
1. Dégustez chaque cuve séparément
2. Faites des micro-assemblages (100ml) en variant les %
3. Laissez reposer 24h avant de re-déguster
4. Validez à l'analyse (équilibre acide/alcool)

Quels cépages avez-vous cette année ?`;
    }
    
    if (q.includes('élevage') || q.includes('fût') || q.includes('barrique')) {
      return `🪵 **Élevage en fût pour Vinsobres**

Pour un Vinsobres de garde, l'élevage en fût est recommandé :

**Durée recommandée** :
- Cuvée classique : **12-14 mois**
- Grande cuvée : **16-18 mois**

**Type de fûts** :
- Privilégiez le **chêne français** (Allier, Tronçais)
- 20-30% de fûts neufs maximum
- Le reste en fûts de 1-3 vins

**Suivi** :
- Ouillage mensuel
- Soutirage tous les 3-4 mois
- Dégustation régulière pour évaluer l'intégration du bois

⚠️ *Attention* : Un excès de bois neuf peut masquer le fruit du Grenache.`;
    }
    
    return `🍷 Excellente question ! 

En tant qu'œnologue spécialisé dans les vins de la **Vallée du Rhône**, je peux vous aider sur ce sujet.

Pour vous donner un conseil précis, pourriez-vous me préciser :
- Le **cépage** concerné (Grenache, Syrah, Mourvèdre...)
- Le **stade** de vinification (vendange, fermentation, élevage, mise)
- Le **type de vin** souhaité (fruité, de garde, léger...)

Je suis là pour vous accompagner dans toutes les étapes de votre vinification ! 🍇`;
  };

  const getStatutColor = (statut) => {
    switch (statut) {
      case 'vide': return 'bg-gray-100 text-gray-600';
      case 'en_remplissage': return 'bg-blue-100 text-blue-700';
      case 'fermentation': return 'bg-orange-100 text-orange-700';
      case 'elevage': return 'bg-purple-100 text-purple-700';
      case 'pret': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getStatutLabel = (statut) => {
    switch (statut) {
      case 'vide': return 'Vide';
      case 'en_remplissage': return 'En remplissage';
      case 'fermentation': return 'Fermentation';
      case 'elevage': return 'Élevage';
      case 'pret': return 'Prêt';
      default: return statut;
    }
  };

  const tabs = [
    { id: 'cuves', label: 'Cuves', icon: Wine, count: cuves.length },
    { id: 'oenologue', label: 'Œnologue IA', icon: Bot, special: true },
    { id: 'intrants', label: 'Intrants', icon: FlaskConical, count: intrants.length },
    { id: 'matieres', label: 'Matières sèches', icon: Package, count: matieres.length },
    { id: 'lots', label: 'Lots & Traçabilité', icon: Tag, count: lots.length },
    { id: 'registre', label: 'Registre de cave', icon: FileText },
    { id: 'alertes', label: 'Alertes IA', icon: AlertTriangle, count: alertes.length },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
        <span className="ml-3 text-gray-600">Chargement de la cave...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Wine className="w-8 h-8 text-purple-600" />
            Cave & Vinification
          </h1>
          <p className="text-gray-500 mt-1">Gestion complète de la cave - Cuves, intrants, traçabilité</p>
        </div>
        <button 
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Wine className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Cuves</p>
              <p className="text-xl font-bold">{stats.total_cuves || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Droplets className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Volume actuel</p>
              <p className="text-xl font-bold">{stats.volume_actuel_hl?.toFixed(0) || 0} hL</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <BarChart3 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Taux remplissage</p>
              <p className="text-xl font-bold">{stats.taux_remplissage || 0}%</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Alertes</p>
              <p className="text-xl font-bold">{alertes.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? tab.special ? 'border-amber-500 text-amber-600 bg-amber-50' : 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              } ${tab.special ? 'font-medium' : ''}`}
            >
              <tab.icon className={`w-4 h-4 ${tab.special ? 'text-amber-500' : ''}`} />
              {tab.label}
              {tab.count > 0 && (
                <span className={`px-2 py-0.5 text-xs rounded-full ${
                  activeTab === tab.id ? 'bg-purple-100' : 'bg-gray-100'
                }`}>
                  {tab.count}
                </span>
              )}
              {tab.special && <Sparkles className="w-3 h-3 text-amber-400" />}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl shadow-sm border">
        
        {/* Œnologue IA Tab */}
        {activeTab === 'oenologue' && (
          <div className="h-[600px] flex flex-col">
            {/* Header œnologue */}
            <div className="p-4 border-b bg-gradient-to-r from-amber-50 to-purple-50">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-purple-600 rounded-full flex items-center justify-center text-2xl">
                  🍷
                </div>
                <div>
                  <h2 className="font-bold text-gray-900">Maître Bacchus</h2>
                  <p className="text-sm text-gray-600">Œnologue IA Expert • Vallée du Rhône</p>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    En ligne
                  </span>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {oenologueMessages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user' 
                      ? 'bg-purple-600 text-white rounded-br-md' 
                      : 'bg-white border shadow-sm rounded-bl-md'
                  }`}>
                    {msg.role === 'assistant' && (
                      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100">
                        <span className="text-lg">🍷</span>
                        <span className="font-medium text-amber-700 text-sm">Maître Bacchus</span>
                      </div>
                    )}
                    <div className={`text-sm whitespace-pre-wrap ${msg.role === 'user' ? '' : 'text-gray-700'}`}>
                      {msg.content.split('**').map((part, i) => 
                        i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {oenologueLoading && (
                <div className="flex justify-start">
                  <div className="bg-white border rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
                      <span className="text-sm text-gray-500">Maître Bacchus réfléchit...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t bg-white">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={oenologueInput}
                  onChange={(e) => setOenologueInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendToOenologue()}
                  placeholder="Posez votre question à l'œnologue..."
                  className="flex-1 px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
                <button
                  onClick={sendToOenologue}
                  disabled={!oenologueInput.trim() || oenologueLoading}
                  className="px-4 py-3 bg-gradient-to-r from-amber-500 to-purple-600 text-white rounded-xl hover:from-amber-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
              <div className="flex gap-2 mt-3">
                <button 
                  onClick={() => setOenologueInput('Quelle température pour la fermentation ?')}
                  className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-full text-gray-600 transition-colors"
                >
                  🌡️ Température fermentation
                </button>
                <button 
                  onClick={() => setOenologueInput('Conseils pour l\'assemblage Vinsobres ?')}
                  className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-full text-gray-600 transition-colors"
                >
                  🍇 Assemblage
                </button>
                <button 
                  onClick={() => setOenologueInput('Dosage SO2 recommandé ?')}
                  className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-full text-gray-600 transition-colors"
                >
                  ⚗️ Sulfitage
                </button>
                <button 
                  onClick={() => setOenologueInput('Conseils élevage en fût ?')}
                  className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-full text-gray-600 transition-colors"
                >
                  🪵 Élevage fût
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Cuves Tab */}
        {activeTab === 'cuves' && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Gestion des Cuves</h2>
              <button className="flex items-center gap-2 px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors">
                <Plus className="w-4 h-4" />
                Nouvelle cuve
              </button>
            </div>
            
            {cuves.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Aucune cuve enregistrée</p>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {cuves.map(cuve => (
                  <div key={cuve.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-lg">{cuve.nom}</h3>
                        <p className="text-sm text-gray-500">{cuve.type_cuve}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatutColor(cuve.statut)}`}>
                        {getStatutLabel(cuve.statut)}
                      </span>
                    </div>
                    
                    {cuve.contenu && (
                      <p className="text-sm text-gray-700 mb-3 bg-gray-50 p-2 rounded">
                        {cuve.contenu}
                      </p>
                    )}
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Volume</span>
                        <span className="font-medium">{cuve.volume_actuel_hl} / {cuve.capacite_hl} hL</span>
                      </div>
                      
                      {/* Barre de remplissage */}
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-purple-600 h-2 rounded-full transition-all"
                          style={{ width: `${(cuve.volume_actuel_hl / cuve.capacite_hl) * 100}%` }}
                        />
                      </div>
                      
                      {cuve.temperature && (
                        <div className="flex items-center gap-2 text-sm">
                          <Thermometer className="w-4 h-4 text-orange-500" />
                          <span>{cuve.temperature}°C</span>
                        </div>
                      )}
                      
                      {cuve.alcool && (
                        <div className="flex items-center gap-2 text-sm">
                          <Grape className="w-4 h-4 text-purple-500" />
                          <span>{cuve.alcool}% vol</span>
                        </div>
                      )}
                      
                      {cuve.so2_libre && (
                        <div className="flex items-center gap-2 text-sm">
                          <Beaker className="w-4 h-4 text-blue-500" />
                          <span>SO₂ libre: {cuve.so2_libre} mg/L</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Intrants Tab */}
        {activeTab === 'intrants' && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Intrants de Vinification</h2>
              <button className="flex items-center gap-2 px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors">
                <Plus className="w-4 h-4" />
                Nouvel intrant
              </button>
            </div>
            
            {intrants.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Aucun intrant enregistré</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Produit</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Stock</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Fournisseur</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Péremption</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {intrants.map(intrant => (
                      <tr key={intrant.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium">{intrant.nom}</p>
                            {intrant.marque && <p className="text-sm text-gray-500">{intrant.marque}</p>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                            {intrant.type_intrant}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`font-medium ${intrant.quantite_stock < 5 ? 'text-red-600' : ''}`}>
                            {intrant.quantite_stock} {intrant.unite}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{intrant.fournisseur || '-'}</td>
                        <td className="px-4 py-3 text-gray-600">{intrant.date_peremption || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Matières sèches Tab */}
        {activeTab === 'matieres' && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Matières Sèches (Embouteillage)</h2>
              <button className="flex items-center gap-2 px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors">
                <Plus className="w-4 h-4" />
                Nouvelle matière
              </button>
            </div>
            
            {matieres.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Aucune matière sèche enregistrée</p>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {matieres.map(matiere => (
                  <div key={matiere.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <Box className="w-5 h-5 text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium">{matiere.nom}</h3>
                        <p className="text-sm text-gray-500">{matiere.type_matiere} {matiere.format && `- ${matiere.format}`}</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Stock</span>
                        <span className={`font-medium ${matiere.quantite_stock < 500 ? 'text-amber-600' : ''}`}>
                          {matiere.quantite_stock?.toLocaleString()} unités
                        </span>
                      </div>
                      {matiere.prix_unitaire && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Prix unitaire</span>
                          <span>{matiere.prix_unitaire?.toFixed(2)} €</span>
                        </div>
                      )}
                      {matiere.fournisseur && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Fournisseur</span>
                          <span>{matiere.fournisseur}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Lots Tab */}
        {activeTab === 'lots' && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Lots & Traçabilité</h2>
              <button className="flex items-center gap-2 px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors">
                <FileText className="w-4 h-4" />
                Exporter registre
              </button>
            </div>
            
            {lots.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Aucun lot enregistré</p>
            ) : (
              <div className="space-y-4">
                {lots.map(lot => (
                  <div key={lot.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <Tag className="w-4 h-4 text-purple-600" />
                          <h3 className="font-semibold">{lot.numero_lot}</h3>
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                            {lot.millesime}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{lot.nom_cuvee || lot.appellation}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        lot.statut === 'pret_mise' ? 'bg-green-100 text-green-700' :
                        lot.statut === 'elevage_fut' ? 'bg-amber-100 text-amber-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {lot.statut?.replace('_', ' ')}
                      </span>
                    </div>
                    
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Cépage</p>
                        <p className="font-medium">{lot.cepage}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Volume initial</p>
                        <p className="font-medium">{lot.volume_initial_hl} hL</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Volume actuel</p>
                        <p className="font-medium">{lot.volume_actuel_hl} hL</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Parcelles</p>
                        <p className="font-medium">{lot.parcelles_origine?.join(', ') || '-'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'registre' && <RegistreCave cuves={cuves} onChange={fetchData} />}

        {/* Alertes IA Tab */}
        {activeTab === 'alertes' && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Alertes & Recommandations IA</h2>
              <button 
                onClick={fetchData}
                className="flex items-center gap-2 px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Actualiser
              </button>
            </div>
            
            {alertes.length === 0 ? (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-3">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
                <p className="text-gray-500">Aucune alerte - Tout est sous contrôle !</p>
              </div>
            ) : (
              <div className="space-y-3">
                {alertes.map((alerte, idx) => (
                  <div 
                    key={idx} 
                    className={`border-l-4 p-4 rounded-r-lg ${
                      alerte.severite === 'haute' ? 'border-red-500 bg-red-50' :
                      alerte.severite === 'moyenne' ? 'border-amber-500 bg-amber-50' :
                      'border-blue-500 bg-blue-50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <AlertTriangle className={`w-5 h-5 mt-0.5 ${
                        alerte.severite === 'haute' ? 'text-red-600' :
                        alerte.severite === 'moyenne' ? 'text-amber-600' :
                        'text-blue-600'
                      }`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{alerte.cuve || alerte.produit}</h3>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            alerte.severite === 'haute' ? 'bg-red-200 text-red-800' :
                            alerte.severite === 'moyenne' ? 'bg-amber-200 text-amber-800' :
                            'bg-blue-200 text-blue-800'
                          }`}>
                            {alerte.severite}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 mt-1">{alerte.message}</p>
                        {alerte.action && (
                          <p className="text-sm font-medium mt-2 text-gray-800">
                            → {alerte.action}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
